import * as React from 'react';
import {
  DragDropContext,
  Draggable,
  DropResult,
  Droppable,
  DroppableProvided,
} from 'react-beautiful-dnd';
import { BaSeBoardProvider } from '../../contexts/board';
import { BaSeTheme } from '../../theme';
import { BaSeLoading } from '../loading/loading';
import { BaSeParagraph } from '../typography/paragraph/paragraph';
import * as Styled from './board-styled';
import { ListWithoutItems } from './list-without-items';
import { BaSeShapeButton } from '../button/shape-button/shape-button';

export type FetchColumnResult = BoardItem[] | Promise<BoardItem[]>;

export interface BoardColumn {
  id: number | string;
  params: any;
  header: HeaderProps;
  items: BoardItems;
  index?: number;
  backgroundColor?: string;
  isDropDisabled?: boolean;
  textWithoutItems?: string;
}

export interface BoardItemEvent {
  item: Pick<BoardItem, 'id'>;
  columnOrigin?: Pick<BoardColumn, 'id' | 'index'>;
  columnTarget?: Pick<BoardColumn, 'id' | 'index'>;
  // type: 'DRAG' | 'DROP' | '...';
}

export interface BoardColumnEvent {
  column: BoardColumn;
  type: 'SCROLL' | 'RELOAD' | 'INITIAL';
}

interface HeaderColumnProps
  extends Pick<BoardColumn, 'header' | 'items'>,
    Pick<HeaderProps, 'hasCompression'> {
  destakForegroundColor?: string;
  destakBackgroundColor?: string;
  onReload(): void;
  handleCompress?: () => void;
  columnCompressed: boolean;
  provided: DroppableProvided;
}

interface ContentItemsProps
  extends Pick<
    HeaderColumnProps,
    'destakForegroundColor' | 'destakBackgroundColor'
  > {
  provided: DroppableProvided;
  column: BoardColumn;
  setColumns: React.Dispatch<React.SetStateAction<BoardColumns>>;
  onFetchColumnItem?: (event: BoardColumnEvent) => FetchColumnResult;
  itemMinWidth?: string | number | undefined;
  itemMaxWidth?: string | number | undefined;
  handleCompressColumn?: () => boolean;
}

export interface BoardItem {
  id: number | string;
  index?: number;
  isDragDisabled?: boolean;
  render: (item: BoardItem) => string | JSX.Element;
}
export type BoardItems = BoardItem[];

export interface HeaderProps {
  title: string;
  subTitle?: string;
  foregroundColor?: string;
  backgroundColor?: string;
  render?: (items: BoardItems) => JSX.Element;
  withCompact?: boolean;
  hasCompression?: boolean;
}

export interface BoardProps
  extends Pick<
    ContentItemsProps,
    'onFetchColumnItem' | 'destakBackgroundColor' | 'destakForegroundColor'
  > {
  columns: BoardColumns;
  itemListMinWidth?: number | string;
  itemListMaxWidth?: number | string;

  onDrop?: (event: BoardItemEvent) => void;

  validateDrag?: (event: BoardItemEvent) => Promise<boolean> | boolean;
  validateDrop?: (event: BoardItemEvent) => Promise<boolean> | boolean;
  handleCompressColumn?: () => boolean;
}

type BoardColumns = BoardColumn[];

const ColumnCompressed: React.FC<HeaderColumnProps> = ({
  header,
  destakBackgroundColor,
  destakForegroundColor,
  columnCompressed,
  handleCompress,
  provided,
}) => {
  const nameIcon = columnCompressed ? 'angle-right' : 'angle-left';
  return (
    <Styled.BoardColumnCompressed
      bgColorHeader={header.backgroundColor ?? destakBackgroundColor}
      {...provided.droppableProps}
      ref={provided.innerRef}
    >
      <>
        <BaSeShapeButton
          buttonType="button"
          color={BaSeTheme.colors.defaultColors.white}
          name={nameIcon}
          onClick={() => handleCompress && handleCompress()}
          shape="square"
          size="medium"
          sizeIcon="medium"
          type="tertiary"
          nameIcon={nameIcon}
        />
      </>
      <Styled.AlignTitleCollumnCompressed>
        <BaSeParagraph
          isBold={true}
          color={
            destakForegroundColor ??
            header.foregroundColor ??
            BaSeTheme.colors.defaultColors.white
          }
        >
          {header.title}
        </BaSeParagraph>
        {header.subTitle && (
          <BaSeParagraph
            color={
              destakForegroundColor ??
              header.foregroundColor ??
              BaSeTheme.colors.defaultColors.white
            }
          >
            {header.subTitle}
          </BaSeParagraph>
        )}
      </Styled.AlignTitleCollumnCompressed>
    </Styled.BoardColumnCompressed>
  );
};

const HeaderColumn: React.FC<HeaderColumnProps> = ({
  header,
  items,
  destakBackgroundColor,
  destakForegroundColor,
  columnCompressed,
  handleCompress,
}) => {
  const nameIcon = columnCompressed ? 'angle-right' : 'angle-left';
  return (
    <Styled.BoardColumnHeader
      bgColorHeader={header.backgroundColor ?? destakBackgroundColor}
    >
      <div style={{ zIndex: 9999999 }}>
        {header.hasCompression && header.hasCompression && (
          <BaSeShapeButton
            buttonType="button"
            color={BaSeTheme.colors.defaultColors.white}
            name={nameIcon}
            onClick={() => handleCompress && handleCompress()}
            shape="square"
            size="medium"
            sizeIcon="medium"
            type="tertiary"
            nameIcon={nameIcon}
          />
        )}
      </div>
      <Styled.AlignHeaderTitle>
        <BaSeParagraph
          isBold={true}
          color={
            destakForegroundColor ??
            header.foregroundColor ??
            BaSeTheme.colors.defaultColors.white
          }
        >
          {header.title}
        </BaSeParagraph>
        {header.subTitle && (
          <BaSeParagraph
            color={
              destakForegroundColor ??
              header.foregroundColor ??
              BaSeTheme.colors.defaultColors.white
            }
          >
            {header.subTitle}
          </BaSeParagraph>
        )}
      </Styled.AlignHeaderTitle>

      {!columnCompressed && header.render && header.render(items)}
    </Styled.BoardColumnHeader>
  );
};

interface ColumnItemsProps extends Pick<BoardColumn, 'items'> {
  loading: boolean;
}
const ColumnItems: React.FC<ColumnItemsProps> = ({ items, loading }) => {
  return (
    <Styled.BoardItemsWrapper>
      {items.map((item, index) => (
        <div key={item.id}>
          <Draggable
            draggableId={item.id.toString()}
            index={index}
            isDragDisabled={item.isDragDisabled}
          >
            {(provided) => {
              return (
                <Styled.BoardItem
                  key={item.id}
                  ref={provided.innerRef}
                  {...provided.draggableProps}
                  {...provided.dragHandleProps}
                >
                  {item.render(item)}
                </Styled.BoardItem>
              );
            }}
          </Draggable>
        </div>
      ))}

      {loading && (
        <div>
          <BaSeLoading
            lightMode="default"
            shadowLevel={0}
            spinnerColor=""
            spinnerSize={24}
            text="Carregando…" // TODO i18n aqui :/
            textColor=""
          />
        </div>
      )}
    </Styled.BoardItemsWrapper>
  );
};

const ContentColumnItem: React.FC<ContentItemsProps> = ({
  provided,
  destakBackgroundColor,
  destakForegroundColor,
  onFetchColumnItem,
  column,
  setColumns,
  itemMaxWidth,
  itemMinWidth,
}) => {
  // const items = React.useMemo(() => column.items ?? [], [column.items]);

  const setItems = React.useCallback(
    (list?: BoardItem[], event?: string) => {
      if (event === 'SCROLL') {
        setColumns((prevColumns) => {
          const newPrev = prevColumns.map((pc) =>
            pc.id === column.id
              ? { ...pc, items: [...pc.items, ...(list ?? [])] }
              : pc,
          );
          return newPrev;
        });
        return;
      }
      setColumns((prevColumns) =>
        prevColumns.map((pc) =>
          pc.id === column.id ? { ...pc, items: list ?? [] } : pc,
        ),
      );
    },
    [column],
  );
  const KEY = `cacheKeyCompress-${column.id}`;

  // const [items, setItems] = React.useState<BoardItem[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [loadingMore, setLoadingMore] = React.useState(false);
  const [hassMoreItems, setHasMoreItems] = React.useState(true);
  const [, setLoadingInitial] = React.useState(false);
  const [typeLoading, setTypeLoading] = React.useState('');
  const [columnCompressed, setColumnCompressed] = React.useState(false);

  const listRef = React.useRef<HTMLDivElement>(null);
  const loadingRef = React.useRef(false);

  const handleCompress = React.useCallback(() => {
    setColumnCompressed((prev) => {
      localStorage.setItem(KEY, JSON.stringify(!prev));
      return !prev;
    });
  }, [KEY]);

  React.useEffect(() => {
    const value = localStorage.getItem(KEY);
    if (value) {
      setColumnCompressed(JSON.parse(value) as boolean);
    }
  }, [KEY]);

  const handleFetchColumnItem = React.useCallback(
    (event: BoardColumnEvent) => {
      if (loadingRef.current) return;
      loadingRef.current = true;
      setLoadingMore(true);

      const result = onFetchColumnItem?.(event) ?? [];
      if (Array.isArray(result)) {
        setItems(result, event.type);
        loadingRef.current = false;
        setLoadingInitial(false);
        setTypeLoading(event.type);
        setHasMoreItems(false);
        return;
      }
      result
        .then((response) => {
          if (response === undefined || response.length === 0) {
            setHasMoreItems(false);
          } else {
            setHasMoreItems(true);
            setLoadingMore(true);
            setItems(response, event.type);
            setTypeLoading(event.type);
          }
        })
        .catch(() => {
          setLoading(false);
          setLoadingMore(false);
        })
        .finally(() => {
          loadingRef.current = false;
          setLoadingMore(false);
          setTypeLoading('FINALLY');
        });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [onFetchColumnItem],
  );

  const handleReload = React.useCallback(() => {
    handleFetchColumnItem({ column, type: 'RELOAD' });
    setTypeLoading('RELOAD');
  }, [handleFetchColumnItem, column]);

  const handleScroll = React.useCallback(() => {
    if (!hassMoreItems) return;
    const list = listRef.current;
    if (list) {
      const some = Number(list.scrollTop + list.clientHeight + 10).toFixed(2);
      const end = Number(some) >= list.scrollHeight;

      if (end) {
        // onFetchColumnItem?.({ column } as BoardItemEvent, onFetchItem);
        handleFetchColumnItem({ column, type: 'SCROLL' });
      }
    }
  }, [handleFetchColumnItem, hassMoreItems, column]);

  React.useEffect(() => {
    const list = listRef.current;
    if (list) {
      list.addEventListener('scroll', handleScroll);

      return () => {
        list.removeEventListener('scroll', handleScroll);
      };
    }

    return undefined;
  });

  React.useEffect(() => {
    if (column.items.length === 0) {
      handleFetchColumnItem({ column, type: 'INITIAL' });
      setTypeLoading('INITIAL');
    }
  }, [handleFetchColumnItem, column.items.length]);

  if (columnCompressed) {
    return (
      <ColumnCompressed
        header={column.header}
        items={column.items}
        destakBackgroundColor={destakBackgroundColor}
        destakForegroundColor={destakForegroundColor}
        onReload={handleReload}
        columnCompressed={columnCompressed}
        handleCompress={handleCompress}
        provided={provided}
      />
    );
  }

  if (typeLoading === 'INITIAL') {
    return (
      <div
        style={{
          maxWidth: `${itemMaxWidth}px`,
          minWidth: `${itemMinWidth}px`,
        }}
        ref={provided.innerRef}
        {...provided.droppableProps}
      >
        <HeaderColumn
          header={column.header}
          items={column.items}
          destakBackgroundColor={destakBackgroundColor}
          destakForegroundColor={destakForegroundColor}
          onReload={handleReload}
          columnCompressed={columnCompressed}
          handleCompress={handleCompress}
          provided={provided}
        />
        <Styled.ContainerLoading>
          <BaSeLoading
            lightMode="default"
            shadowLevel={0}
            spinnerColor=""
            spinnerSize={24}
            text="Carregando…"
            textColor=""
          />
        </Styled.ContainerLoading>
      </div>
    );
  }

  return (
    <div
      style={{
        maxWidth: `${itemMaxWidth}px`,
        minWidth: `${itemMinWidth}px`,
      }}
    >
      <HeaderColumn
        header={column.header}
        items={column.items}
        destakBackgroundColor={destakBackgroundColor}
        destakForegroundColor={destakForegroundColor}
        onReload={handleReload}
        columnCompressed={columnCompressed}
        handleCompress={handleCompress}
        provided={provided}
      />

      <Styled.BoardItemListWrapper loading={loading}>
        {column.items.length > 0 ? (
          <div {...provided.droppableProps} ref={provided.innerRef}>
            <Styled.BoardColumnContent
              id="colunascrool"
              ref={listRef}
              bgColor={column.backgroundColor}
            >
              <ColumnItems items={column.items} loading={loadingMore} />

              {provided.placeholder}
            </Styled.BoardColumnContent>
          </div>
        ) : (
          <ListWithoutItems column={column} provided={provided} />
        )}
      </Styled.BoardItemListWrapper>
    </div>
  );
};

const BoardWrapper: React.FC<React.PropsWithChildren> = ({ children }) => (
  <BaSeBoardProvider>{children}</BaSeBoardProvider>
);

export const BaSeBoard: React.FC<BoardProps> = ({
  columns: columnsExternal,
  itemListMinWidth,
  itemListMaxWidth,
  onFetchColumnItem,
  validateDrop,
  onDrop,
  handleCompressColumn,
}) => {
  const [columns, setColumns] = React.useState(columnsExternal);
  const [loadingValidDrop, setLoadingValidDrop] = React.useState(false);

  React.useEffect(() => {
    setColumns(columnsExternal);
  }, [columnsExternal]);

  const commitDrop = React.useCallback(
    (result: DropResult, boardItemEvent: BoardItemEvent) => {
      setColumns((prevColumns) => {
        const updatedColumns = [...prevColumns];

        const sourceColumn = updatedColumns.filter((column) =>
          column.items.some((item) => item.id === Number(result.draggableId)),
        )[0];

        const targetColumn = updatedColumns.filter(
          (column) => column.id === Number(result.destination?.droppableId),
        )[0];
        const itemToMove = sourceColumn?.items.filter(
          (item) => item.id === Number(result.draggableId),
        )[0];

        sourceColumn.items = sourceColumn.items.filter(
          (item) => item.id !== Number(result.draggableId),
        );

        const destinationIndex = result.destination?.index || 0;
        targetColumn.items = [
          ...targetColumn.items.slice(0, destinationIndex),
          itemToMove,
          ...targetColumn.items.slice(destinationIndex),
        ];
        return updatedColumns;
      });
      onDrop?.(boardItemEvent);
    },
    [onDrop],
  );

  const handleDragEnd = React.useCallback(
    (result: DropResult) => {
      if (!result.destination) {
        return;
      }
      if (result.destination?.droppableId === undefined) {
        return;
      }
      if (result.source.droppableId === result.destination.droppableId) {
        return;
      }

      const boardItemEvent: BoardItemEvent = {
        item: { id: result.draggableId },
        columnOrigin: { id: result.source.droppableId },
        columnTarget: { id: result.destination.droppableId },
      };

      const resultValidateDrop = validateDrop?.(boardItemEvent) ?? true;

      if (typeof resultValidateDrop === 'boolean') {
        if (resultValidateDrop) {
          commitDrop(result, boardItemEvent);
        }
        return;
      }

      setLoadingValidDrop(true);
      resultValidateDrop
        .then((bool) => {
          if (bool) {
            commitDrop(result, boardItemEvent);
          }
        })
        .catch((error) => {
          console.log(error);
        })
        .finally(() => setLoadingValidDrop(false));
    },
    [commitDrop, validateDrop],
  );

  return (
    <BoardWrapper>
      <DragDropContext onDragEnd={handleDragEnd}>
        <Styled.BoardListsWrapper
          length={columns.length}
          itemMinWidth={itemListMinWidth}
          itemMaxWidth={itemListMaxWidth}
          loadingValidDrop={loadingValidDrop}
        >
          <Styled.BoardColumnContainer>
            {columns.map((column) => (
              <Droppable
                key={column.id}
                type="COLUMN"
                direction="vertical"
                droppableId={String(column.id)}
              >
                {(provided) => (
                  <ContentColumnItem
                    onFetchColumnItem={onFetchColumnItem}
                    provided={provided}
                    column={column}
                    setColumns={setColumns}
                    itemMinWidth={itemListMinWidth}
                    itemMaxWidth={itemListMaxWidth}
                    handleCompressColumn={handleCompressColumn}
                  />
                )}
              </Droppable>
            ))}
          </Styled.BoardColumnContainer>
        </Styled.BoardListsWrapper>
      </DragDropContext>
    </BoardWrapper>
  );
};
